import type { ReactNode } from "react";
import { useEffect, useRef, useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { useVenueAuth } from "../contexts/VenueAuthContext";
import { useVenue } from "../contexts/VenueContext";
import LoadingSpinner from "./LoadingSpinner";
import NoVenueAssignedPage from "./NoVenueAssignedPage";
import { getSupabase } from "@dwelldrop/supabase";
import { isAuthenticationError, useOnlineStatus } from "@dwelldrop/shared";
import {
  fetchPublishedDpaPolicy,
  VENUE_SUPABASE_STORAGE_KEY,
} from "../lib/dpaPolicy";
import { useVenueConsentGate } from "../hooks/useVenueConsentGate";
import VenueConsentModal from "./modals/VenueConsentModal";

type DpaGateState =
  | { state: "idle" }
  | { state: "checking"; venueId: string; requiredVersion: string }
  | { state: "ok"; venueId: string; requiredVersion: string; checkedAt: number }
  | {
      state: "required";
      venueId: string;
      requiredVersion: string;
      checkedAt: number;
    }
  | {
      state: "error";
      venueId: string;
      requiredVersion: string;
      checkedAt: number;
      message: string;
    };

type DpaStatusRow = {
  id: string;
  agreement_version: string;
  status: "active" | "terminated";
};

const DPA_PATH = "/dpa";
const USABLE_VENUE_STATUSES = new Set(["active", "pending"]);

async function fetchActiveDpaStatusForVenue(
  venueId: string
): Promise<DpaStatusRow | null> {
  const supabase = getSupabase(VENUE_SUPABASE_STORAGE_KEY);

  // Prefer RPC when available, but fall back to direct table read because
  // PostgREST schema cache may not include newly added functions yet.
  const rpc = await supabase.rpc("get_dpa_status", { p_venue_id: venueId });
  if (!rpc.error) {
    const row = (Array.isArray(rpc.data) ? rpc.data[0] : rpc.data) as
      | DpaStatusRow
      | null
      | undefined;
    return row ?? null;
  }

  if (rpc.error.code !== "PGRST202") {
    const err = new Error(rpc.error.message);
    (err as unknown as { code?: string; status?: number }).code = rpc.error.code;
    (err as unknown as { code?: string; status?: number }).status = rpc.status;
    throw err;
  }

  const query = await supabase
    .from("data_processing_agreements")
    .select("id, agreement_version, status")
    .eq("venue_id", venueId)
    .eq("status", "active")
    .order("signed_at", { ascending: false })
    .limit(1)
    .maybeSingle();

  if (query.error) {
    const err = new Error(query.error.message);
    (err as unknown as { code?: string; status?: number }).code = query.error.code;
    (err as unknown as { code?: string; status?: number }).status = query.status;
    throw err;
  }
  return (query.data as DpaStatusRow | null) ?? null;
}

export default function VenueAccessGate({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { profile, signOut } = useVenueAuth();
  const { currentVenue, venues, loading } = useVenue();
  const { isOnline } = useOnlineStatus();
  const [dpaGate, setDpaGate] = useState<DpaGateState>({ state: "idle" });
  const prevPathRef = useRef<string>(location.pathname);
  // Tracks the last time we completed an "ok" check, separate from dpaGate.checkedAt
  // so that reducing the re-check interval doesn't cause effect re-entrancy loops.
  const lastOkCheckedAtRef = useRef<number>(0);
  const inFlightCheckRef = useRef<string | null>(null);
  const [requiredDpaVersion, setRequiredDpaVersion] = useState(() => {
    return (
      (import.meta.env.VITE_DPA_REQUIRED_VERSION as string | undefined) ?? "1.0"
    );
  });

  // Venue legal consent gate (terms/privacy). Only evaluated once the user is
  // online and not on the standalone DPA signing route; dpa has its own gate.
  const consentGateEnabled = isOnline && location.pathname !== DPA_PATH;
  const {
    needsConsent,
    consentData,
    refresh: refreshConsent,
  } = useVenueConsentGate(consentGateEnabled);

  // Safety guard: if a non-usable venue slips through, force suspended flow
  // instead of allowing unrelated redirects (for example, DPA gate).
  useEffect(() => {
    if (loading) return;
    if (!currentVenue) return;
    if (profile?.role === "property_operator") return;
    if (USABLE_VENUE_STATUSES.has(currentVenue.status)) return;

    void signOut().finally(() => {
      navigate("/login?suspended=true", { replace: true });
    });
  }, [currentVenue, loading, navigate, profile?.role, signOut]);

  // Fix A (TEC-111): when owner navigates away from /dpa after signing, reset
  // gate to idle so a fresh DB check runs instead of the render immediately
  // bouncing them back via the stale "required" state.
  useEffect(() => {
    const prev = prevPathRef.current;
    prevPathRef.current = location.pathname;
    if (prev === DPA_PATH && location.pathname !== DPA_PATH) {
      setDpaGate({ state: "idle" });
    }
  }, [location.pathname]);

  // Fix B (TEC-111): detect admin-published DPA version bumps in real-time.
  //   1. Supabase Realtime — fires immediately on any device when admin UPDATEs
  //      the dpa_policy row in app_settings.
  //   2. setInterval 30s — fallback if Realtime connection is down; expires the
  //      check cache so the next navigation triggers a fresh DB check.
  useEffect(() => {
    if (profile?.role !== "venue_owner") return;

    const resetGate = () => {
      lastOkCheckedAtRef.current = 0;
      setDpaGate((prev) => (prev.state === "ok" ? { state: "idle" } : prev));
    };

    const supabase = getSupabase(VENUE_SUPABASE_STORAGE_KEY);

    const channel = supabase
      .channel("dpa-version-watch")
      .on(
        "postgres_changes",
        {
          event: "UPDATE",
          schema: "public",
          table: "consent_types",
          filter: "name=eq.dpa", // 🔥 QUAN TRỌNG
        },
        (payload) => {
          const newVersion = payload.new?.version;
          const oldVersion = payload.old?.version;

          if (newVersion !== oldVersion) {
            resetGate(); // chỉ reset khi version đổi
          }
        }
      )
      .subscribe();

    const interval = setInterval(() => {
      lastOkCheckedAtRef.current = 0;
    }, 30 * 1000);

    return () => {
      void channel.unsubscribe();
      clearInterval(interval);
    };
  }, [profile?.role]);

  // Owner DPA gate: after login, require an active DPA for the selected venue.
  // If not signed (or version mismatch), force user onto /dpa.
  useEffect(() => {
    if (loading) return;
    if (!currentVenue) return;
    if (profile?.role !== "venue_owner") return;
    if (!isOnline) return;

    const venueId = currentVenue.id;
    const onDpaSigningRoute = location.pathname === DPA_PATH;
    const stale =
      dpaGate.state === "idle"
        ? true
        : dpaGate.venueId !== venueId ||
          dpaGate.requiredVersion !== requiredDpaVersion;

    // If we've already verified this venue+version is OK, allow — but still
    // re-check when the cache expires (30s interval or tab focus) so
    // admin-published version bumps are detected without a reload (Fix B).
    if (!stale && dpaGate.state === "ok") {
      const RECHECK_INTERVAL_MS = 30 * 1000;
      if (Date.now() - lastOkCheckedAtRef.current < RECHECK_INTERVAL_MS) return;
      // Fall through to check() — will detect version mismatch if present.
    }

    // If a check is already in flight for this venue+version, don't start another.
    if (!stale && dpaGate.state === "checking") return;

    // If we already know DPA is required (or we hit an error) for this venue+version,
    // avoid spamming checks. Still allow periodic re-checks so that after the user
    // signs (in another tab or after a fast redirect), access can unlock without
    // a hard refresh.
    if (!stale && (dpaGate.state === "required" || dpaGate.state === "error")) {
      const checkedAt = "checkedAt" in dpaGate ? dpaGate.checkedAt : 0;
      const allowRecheck = Date.now() - checkedAt > 2500;
      if (!allowRecheck) return;
    }

    async function check() {
      if (inFlightCheckRef.current === venueId) return;
      inFlightCheckRef.current = venueId;
      setDpaGate({
        state: "checking",
        venueId,
        requiredVersion: requiredDpaVersion,
      });
      try {
        const policy = await fetchPublishedDpaPolicy();
        if (policy.version !== requiredDpaVersion) {
          setRequiredDpaVersion(policy.version);
        }
        const row = await fetchActiveDpaStatusForVenue(venueId);
        const ok =
          !!row &&
          row.status === "active" &&
          (row.agreement_version ?? "") === policy.version;

        if (ok) {
          lastOkCheckedAtRef.current = Date.now();
          setDpaGate({
            state: "ok",
            venueId,
            requiredVersion: policy.version,
            checkedAt: Date.now(),
          });
          return;
        }

        setDpaGate({
          state: "required",
          venueId,
          requiredVersion: policy.version,
          checkedAt: Date.now(),
        });
        if (!onDpaSigningRoute) {
          navigate(DPA_PATH, { replace: true });
        }
      } catch (err) {
        const isNetworkError =
          !isOnline ||
          (err instanceof TypeError &&
            /fetch|network/i.test(err.message));

        if (isNetworkError) {
          setDpaGate({
            state: "error",
            venueId,
            requiredVersion: requiredDpaVersion,
            checkedAt: Date.now(),
            message: "offline",
          });
          return;
        }

        // If the failure is due to expired JWT, revoked session (e.g. admin 2FA reset),
        // or unauthorized access, sign out cleanly and send user to login.
        if (isAuthenticationError(err)) {
          void signOut().finally(() => {
            navigate("/login?expired=true", { replace: true });
          });
          return;
        }

        // Even if the error wasn't matched by code/message, verify if user session
        // is still valid on the server. If revoked/expired, sign out.
        try {
          const supabase = getSupabase(VENUE_SUPABASE_STORAGE_KEY);
          const { data: userData, error: userErr } =
            await supabase.auth.getUser();
          if (userErr || !userData?.user) {
            void signOut().finally(() => {
              navigate("/login?expired=true", { replace: true });
            });
            return;
          }
        } catch {
          void signOut().finally(() => {
            navigate("/login?expired=true", { replace: true });
          });
          return;
        }

        setDpaGate({
          state: "error",
          venueId,
          requiredVersion: requiredDpaVersion,
          checkedAt: Date.now(),
          message: "Failed to check DPA status.",
        });
      } finally {
        inFlightCheckRef.current = null;
      }
    }

    void check();
    return;
  }, [
    currentVenue,
    dpaGate,
    loading,
    location.pathname,
    navigate,
    profile?.role,
    requiredDpaVersion,
    isOnline,
    signOut,
  ]);

  if (loading) {
    return <LoadingSpinner />;
  }

  if (
    profile?.role !== "property_operator" &&
    currentVenue &&
    !USABLE_VENUE_STATUSES.has(currentVenue.status)
  ) {
    return <LoadingSpinner />;
  }

  if (profile?.role !== "property_operator" && !currentVenue) {
    if (loading || venues.length > 0) {
      return <LoadingSpinner />;
    }
    return (
      <NoVenueAssignedPage
        onSignOut={async () => {
          await signOut();
          navigate("/login");
        }}
      />
    );
  }

  if (profile?.role === "venue_owner" && location.pathname !== DPA_PATH) {
    const isOfflineError =
      dpaGate.state === "error" && dpaGate.message === "offline";
    if (
      (!isOnline || isOfflineError) &&
      dpaGate.state !== "ok"
    ) {
      return <>{children}</>;
    }

    if (dpaGate.state === "idle" || dpaGate.state === "checking") {
      return <LoadingSpinner />;
    }

    if (dpaGate.state === "required") {
      return <Navigate to={DPA_PATH} replace />;
    }

    if (dpaGate.state === "error") {
      return (
        <div className="flex min-h-screen items-center justify-center bg-gray-50 px-4">
          <div className="w-full max-w-md rounded-xl border border-gray-200 bg-white p-6">
            <h2 className="mb-2 text-lg font-semibold text-gray-900">
              Unable to verify DPA status
            </h2>
            <p className="mb-4 text-sm text-gray-600">{dpaGate.message}</p>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setDpaGate({ state: "idle" })}
                className="flex-1 rounded-lg border border-gray-300 px-4 py-2 text-sm hover:bg-gray-50"
              >
                Retry
              </button>
              <button
                type="button"
                onClick={async () => {
                  await signOut();
                  navigate("/login");
                }}
                className="flex-1 rounded-lg bg-gray-900 px-4 py-2 text-sm text-white hover:bg-gray-800"
              >
                Sign out
              </button>
            </div>
          </div>
        </div>
      );
    }
  }

  if (consentGateEnabled && needsConsent && consentData) {
    return (
      <VenueConsentModal consentData={consentData} onComplete={refreshConsent} />
    );
  }

  return <>{children}</>;
}
